import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ChevronDown, ChevronRight, Copy } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Box, CircularProgress } from "@mui/material";
import toast from "react-hot-toast";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import AdsAPIs from "../../apiRoutes/Ads";
import { GET_AD_REQUEST_QUERY_KEY } from "../../utils/constants/queryKeys";
import StatusChip from "../common/StatusChip";
import GlobalAppBadge from "../common/AppSwitch/GlobalAppBadge";
import QcHistory from "../common/QcHistory";
import ModerationRounds from "./ModerationRounds";
import LaunchDetails from "./LaunchDetails";
import QcReview, { qcRollup } from "./QcReview";
import DeliveredVideo from "./DeliveredVideo";
import {
  formatDisplayDate,
  formatDisplayDateTime,
} from "../../utils/helperFunctions";
import usePermissions from "../../hooks/usePermissions";
import useGlobalAppAndLanguage from "../../hooks/useGlobalAppAndLanguage";
import { PERMISSION_CODES } from "../../utils/constants/permissions";
import { GET_AD_REQUESTS_QUERY_KEY } from "../../utils/constants/queryKeys";
import { Button } from "../ui/button";
import EditAdRequestModal from "./Modals/EditAdRequestModal";
import { QC_STATUS_CHIP } from "../../utils/constants/index";

/* ─── small reusable primitives ─────────────────────────────────────────── */

const MetaTag = ({ label, value }) => {
  if (!value) return null;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-gray-500">
      <span className="font-medium text-gray-400 uppercase tracking-wide">
        {label}
      </span>
      <span className="text-gray-600">{value}</span>
    </span>
  );
};

const Pill = ({ children, color = "gray" }) => {
  const colors = {
    purple: "bg-purple-100 text-purple-700 border-purple-200",
    blue: "bg-blue-100 text-blue-700 border-blue-200",
    green: "bg-green-100 text-green-700 border-green-200",
    gray: "bg-gray-100 text-gray-600 border-gray-200",
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${colors[color]}`}
    >
      {children}
    </span>
  );
};

// Splits a string by commas, renders each URL as an inline hyperlink
const LinkList = ({ value, label }) => {
  if (!value) return <span className="text-sm italic text-gray-300">—</span>;
  const parts = value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return (
    <div className="flex flex-col gap-0.5">
      {parts.map((part, i) => {
        const isUrl = /^https?:\/\//i.test(part);
        return isUrl ? (
          <div key={i} className="flex flex-col gap-0.5">
            <a
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primaryPurple hover:underline"
            >
              {label && parts.length > 1 ? `${label} ${i + 1}` : label || part}
            </a>
            <span className="text-xs text-gray-400 break-all">{part}</span>
          </div>
        ) : (
          <span key={i} className="text-sm text-gray-800 break-all">
            {part}
          </span>
        );
      })}
    </div>
  );
};

const LabelValue = ({ label, value, emptyLabel = "—" }) => {
  const isEmpty = !value;
  return (
    <div className="min-w-0">
      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">
        {label}
      </p>
      <p
        className={`text-sm break-words ${isEmpty ? "text-gray-300 italic" : "text-gray-800"}`}
      >
        {isEmpty ? emptyLabel : value}
      </p>
    </div>
  );
};

const Divider = () => <div className="border-t border-gray-100 my-4" />;

const SectionTitle = ({ children }) => (
  <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">
    {children}
  </p>
);

/** One foldable block of the detail page. The page owns the open state so the section nav can expand a target. */
const CollapsibleSection = ({
  id,
  title,
  subtitle,
  open,
  onToggle,
  children,
}) => {
  return (
    <div
      id={id}
      className="mb-3 scroll-mt-16 overflow-hidden rounded-lg border border-gray-200"
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 bg-gray-50 px-4 py-2.5 text-left transition hover:bg-gray-100"
      >
        <span className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-sm font-semibold text-gray-800">{title}</span>
          {subtitle && (
            <span className="text-xs text-gray-500">{subtitle}</span>
          )}
        </span>
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-gray-500" />
        )}
      </button>
      {open && <div className="px-4 py-4">{children}</div>}
    </div>
  );
};

/** Nearest scrollable ancestor: the page scrolls inside MainLayout's #HideScroll, not the window. */
const getScrollParent = (node) => {
  let el = node?.parentElement;
  while (el) {
    const oy = getComputedStyle(el).overflowY;
    if (oy === "auto" || oy === "scroll") return el;
    el = el.parentElement;
  }
  return null;
};

const NAV_OFFSET = 56;

const SectionNav = ({ sections, activeId, onJump }) => {
  if (sections.length < 2) return null;
  return (
    <div className="sticky top-0 z-20 -mx-4 mb-3 border-b border-gray-200 bg-white/95 px-4 backdrop-blur md:-mx-6 md:px-6 xl:hidden">
      <div className="flex gap-1 overflow-x-auto py-2">
        {sections.map((s) => {
          const active = s.id === activeId;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onJump(s.id)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition ${
                active
                  ? "bg-primaryPurple text-white"
                  : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
              }`}
            >
              {s.label}
              {s.count != null && (
                <span
                  className={
                    active ? "ml-1.5 opacity-80" : "ml-1.5 text-gray-400"
                  }
                >
                  {s.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/** Floating table of contents: dashes at rest, full list on hover. */
const SectionRail = ({ sections, activeId, onJump }) => {
  const [open, setOpen] = useState(false);
  if (sections.length < 2) return null;
  return (
    <div
      className="fixed right-4 top-1/2 z-30 hidden -translate-y-1/2 xl:block"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {open ? (
        <div className="w-72 overflow-hidden rounded-xl border border-gray-200 bg-white pb-2 shadow-2xl ring-1 ring-black/5">
          <p className="border-b border-gray-100 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
            On this page
          </p>
          {sections.map((s) => {
            const active = s.id === activeId;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => onJump(s.id)}
                className={`mt-1 flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm transition ${
                  active
                    ? "bg-primaryPurple/10 font-semibold text-primaryPurple"
                    : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                }`}
              >
                <span className="truncate">{s.label}</span>
                {s.count != null && (
                  <span
                    className={`shrink-0 rounded-full px-1.5 py-0.5 text-[11px] ${
                      active
                        ? "bg-primaryPurple/15 text-primaryPurple"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {s.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        /* Padding widens the hover target beyond the dashes themselves. */
        <div className="flex cursor-pointer flex-col items-end gap-3 py-2 pl-8 pr-1">
          {sections.map((s) => (
            <span
              key={s.id}
              className={`h-[3px] rounded-full transition-all duration-200 ${
                s.id === activeId ? "w-8 bg-primaryPurple" : "w-5 bg-gray-300"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── main component ─────────────────────────────────────────────────────── */

const AdRequestDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  // "default" means the first entry of the session: nothing of ours behind it.
  const canGoBack = useLocation().key !== "default";
  // Route param is a string; the id feeds a query key and the QC endpoints, so
  // normalise it once here rather than at every call site.
  const requestId = Number(id) || null;
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canQc = hasPermission(PERMISSION_CODES.AD_QC_REVIEW);
  const canEdit = hasPermission(PERMISSION_CODES.AD_REQUEST_UPDATE);
  const [qcBusy, setQcBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  // The request list (QC column) and the deliveries page both reflect the new
  // state, so refresh all three.
  const qcMutation = useMutation({
    mutationFn: ({ action, items }) =>
      AdsAPIs.qcRequestAction({ axiosPrivate, requestId, action, items }),
    onSuccess: (_data, variables) => {
      const sentBack = variables.items?.length ?? 0;
      toast.success(
        variables.action === "approve"
          ? "Approved — sent to moderation."
          : `${sentBack} ${sentBack === 1 ? "video" : "videos"} sent back — the agency will see your notes.`
      );
      queryClient.invalidateQueries({
        queryKey: [...GET_AD_REQUEST_QUERY_KEY, requestId],
      });
      queryClient.invalidateQueries({ queryKey: GET_AD_REQUESTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["adDeliveries"] });
    },
    onError: (e) =>
      toast.error(
        e?.response?.data?.error_message || e?.message || "QC action failed"
      ),
    onSettled: () => setQcBusy(false),
  });

  const handleQcAction = (action, items) => {
    setQcBusy(true);
    qcMutation.mutate({ action, items });
  };
  const { data: detail, isLoading } = useQuery({
    queryKey: [...GET_AD_REQUEST_QUERY_KEY, requestId],
    queryFn: () => AdsAPIs.getAdRequest({ axiosPrivate, id: requestId }),
    // Was `open && !!requestId` when this was a modal; on a page the route
    // being mounted IS the open condition.
    enabled: !!requestId,
  });
  const {
    globalApp,
    globalLanguage,
    handleGlobalLabel,
    handleSwitchGlobalApp,
  } = useGlobalAppAndLanguage();

  useEffect(() => {
    const target = {
      app: (detail?.ad?.for_app_name || "").toLowerCase(),
      language: (detail?.language || detail?.ad?.language || "").toLowerCase(),
    };
    if (!target.app || !target.language) return;
    if (target.app === globalApp && target.language === globalLanguage) return;
    if (handleSwitchGlobalApp(target))
      toast(`Switched to ${handleGlobalLabel(target)} to match this request`);
  }, [detail, globalApp, globalLanguage, handleGlobalLabel, handleSwitchGlobalApp]);

  // Deliveries that actually have a video to show/QC.
  const launchedDeliveries = (detail?.deliveries ?? []).filter((d) => d.launch);
  const moderatedDeliveries = (detail?.deliveries ?? []).filter(
    (d) => Array.isArray(d.moderations) && d.moderations.length > 0
  );
  const playableDeliveries = (detail?.deliveries ?? []).filter((d) =>
    (d.s3_asset_url || d.ad_asset_url || "").trim()
  );

  const sections = [
    { id: "sec-request", label: "Request details" },
    playableDeliveries.length && {
      id: "sec-qc",
      label: canQc ? "Delivery QC" : "Videos",
      count: playableDeliveries.length,
    },
    moderatedDeliveries.length && {
      id: "sec-moderation",
      label: "Moderation",
      count: moderatedDeliveries.length,
    },
    launchedDeliveries.length && {
      id: "sec-launch",
      label: "Launch",
      count: launchedDeliveries.length,
    },
  ].filter(Boolean);
  const sectionKey = sections.map((x) => x.id).join(",");

  const [closedIds, setClosedIds] = useState(() => new Set());
  const toggleSection = useCallback((id) => {
    setClosedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const [activeId, setActiveId] = useState(null);

  const jumpTo = useCallback((id) => {
    setClosedIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    // Two frames: let an expanding section grow the scroll height first.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        const container = getScrollParent(el);
        if (!el || !container) return;
        const top =
          container.scrollTop +
          el.getBoundingClientRect().top -
          container.getBoundingClientRect().top -
          NAV_OFFSET;
        container.scrollTo({ top, behavior: "smooth" });
      })
    );
  }, []);

  useEffect(() => {
    if (!sectionKey) return undefined;
    const ids = sectionKey.split(",");
    const container = getScrollParent(document.getElementById(ids[0]));
    if (!container) return undefined;
    let raf = 0;
    const update = () => {
      raf = 0;
      const cTop = container.getBoundingClientRect().top;
      let current = ids[0];
      ids.forEach((id) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top - cTop <= NAV_OFFSET + 12)
          current = id;
      });
      // At the bottom the last section may be too short to cross the line.
      if (
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 4
      )
        current = ids[ids.length - 1];
      setActiveId(current);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    container.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [sectionKey]);

  const topic = detail?.ad?.topic ?? detail?.topic ?? null;

  // The Ad's filled name once delivered, else the request's template.

  const adsetName = detail?.ad?.adset_name || detail?.adset_name || "";
  const adId = detail?.ad_id ?? detail?.ad?.id ?? null;
  const dueDate = detail?.due_delivery_date
    ? formatDisplayDate(detail.due_delivery_date)
    : null;
  const requestedVia =
    detail?.requested_via === "single_entry"
      ? "Single entry"
      : detail?.requested_via === "bulk_entry"
        ? "Bulk entry"
        : detail?.requested_via === "agency_direct"
          ? "Agency direct"
          : (detail?.requested_via ?? null);

  return (
    <div
      className="px-4 py-5 md:px-6 bg-white"
      style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}
    >
      {/* Reached from Ad Request, Ad Deliver and Ad Launch, so go back. */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <button
            type="button"
            onClick={() =>
              canGoBack ? navigate(-1) : navigate("/dashboard/ad-request")
            }
            className="text-xs font-medium text-primaryPurple hover:underline"
          >
            {canGoBack ? "← Back" : "← Back to ad requests"}
          </button>
          <h1 className="mt-1 text-lg font-semibold text-gray-900">
            Ad request details
          </h1>
        </div>
        {canEdit && detail?.status === "pending" && (
          <Button variant="outline" onClick={() => setEditOpen(true)}>
            Edit request
          </Button>
        )}
      </div>

      <EditAdRequestModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        request={detail}
      />

      <div className="min-h-[200px] pb-4">
        {!requestId ? (
          <p className="text-sm text-gray-500">No request selected.</p>
        ) : isLoading ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            py={4}
            gap={2}
          >
            <CircularProgress size={24} sx={{ color: "#7f7ad5" }} />
            <span className="text-sm text-gray-600">Loading…</span>
          </Box>
        ) : !detail ? (
          <p className="text-sm text-gray-500">Failed to load details.</p>
        ) : (
          <div>
            {/* ── 1. HERO STRIP ─ status / priority / id / due date ──────── */}
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 mb-4 rounded-lg bg-gray-50 border border-gray-200">
              <div className="flex flex-wrap items-center gap-2">
                {adId && (
                  <span className="text-sm text-gray-500">
                    ID&nbsp;
                    <span className="font-semibold text-gray-700">#{adId}</span>
                  </span>
                )}
                {detail.ad?.for_app_name && (
                  <GlobalAppBadge
                    app={detail.ad.for_app_name}
                    language={detail.language || detail.ad.language}
                  />
                )}
                <StatusChip
                  status={
                    detail.status === "approved" ? "success" : detail.status
                  }
                  size="small"
                />
                <StatusChip status={detail.priority} size="small" />
                {playableDeliveries.length > 0 && (
                  <StatusChip
                    status={QC_STATUS_CHIP[qcRollup(playableDeliveries)] ?? "pending"}
                    size="small"
                  />
                )}
                {detail.requested_via === "agency_direct" && (
                  <Pill color="purple">Delivered without request</Pill>
                )}
                {detail.sprint_plan_assigned && (
                  <Pill color="purple">Sprint assigned</Pill>
                )}
                {detail.ad_type?.name && (
                  <Pill color="blue">{detail.ad_type.name}</Pill>
                )}
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                {dueDate && (
                  <span>
                    Due&nbsp;
                    <span className="font-semibold text-gray-700">
                      {dueDate}
                    </span>
                  </span>
                )}
              </div>
            </div>

            {/* ── 2. TOPIC ────────────────────────────────────────────────── */}
            {topic ? (
              <h3 className="text-base font-semibold text-gray-900 leading-snug mb-1 px-1">
                {topic}
              </h3>
            ) : (
              <p className="text-sm italic text-gray-300 mb-1 px-1">No topic</p>
            )}

            {adsetName && (
              <div className="mb-4 flex items-start justify-between gap-3 rounded-xl border border-[#e3e0fa] bg-gradient-to-br from-[#efedfc] via-white to-white px-4 py-3 shadow-card">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-secondaryDarkPurple">Ad set name</p>
                  <p className="mt-1 break-all font-mono text-sm leading-6 text-gray-900">{adsetName}</p>
                </div>
                <button
                  type="button"
                  onClick={() => navigator.clipboard?.writeText(adsetName).then(() => toast.success("Copied"))}
                  title="Copy"
                  className="mt-0.5 shrink-0 rounded-md p-1.5 text-gray-400 hover:bg-white hover:text-gray-700"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </div>
            )}

            {/* Two collapsible sections: everything describing the request,
                then everything about QC. The page had grown to nine stacked
                blocks and reviewers could not find the videos in it. */}
            <SectionNav
              sections={sections}
              activeId={activeId}
              onJump={jumpTo}
            />
            <SectionRail
              sections={sections}
              activeId={activeId}
              onJump={jumpTo}
            />

            <CollapsibleSection
              id="sec-request"
              title="Request details"
              open={!closedIds.has("sec-request")}
              onToggle={() => toggleSection("sec-request")}
            >
              {/* ── 3. TARGETING PILLS ─ App / Module / Account ─────────────── */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-2 mb-4 px-1">
                {detail.ad?.for_app_name && (
                  <span className="inline-flex items-center gap-1">
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                      App
                    </span>
                    <Pill>{detail.ad.for_app_name}</Pill>
                  </span>
                )}
                {detail.ad?.module && (
                  <span className="inline-flex items-center gap-1">
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                      Module
                    </span>
                    <Pill>{detail.ad.module}</Pill>
                  </span>
                )}
                {detail.ad?.account && (
                  <span className="inline-flex items-center gap-1">
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                      Account
                    </span>
                    <Pill>{detail.ad.account}</Pill>
                  </span>
                )}
                {detail.ad?.category && (
                  <span className="inline-flex items-center gap-1 text-xs">
                    <span className="font-semibold text-gray-400 uppercase tracking-wide text-[10px]">
                      Category
                    </span>
                    <span className="text-gray-600">{detail.ad.category}</span>
                  </span>
                )}
                {detail.ad?.track && (
                  <span className="inline-flex items-center gap-1 text-xs">
                    <span className="font-semibold text-gray-400 uppercase tracking-wide text-[10px]">
                      Track
                    </span>
                    <span className="text-gray-600">{detail.ad.track}</span>
                  </span>
                )}
                {detail.ad?.track_number != null && (
                  <span className="inline-flex items-center gap-1 text-xs">
                    <span className="font-semibold text-gray-400 uppercase tracking-wide text-[10px]">
                      Track #
                    </span>
                    <span className="text-gray-600">
                      {detail.ad.track_number}
                    </span>
                  </span>
                )}
              </div>

              {/* ── 4. SCRIPT ───────────────────────────────────────────────── */}
              {detail.script_name && (
                <div className="mb-4">
                  <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                    Script name
                  </p>
                  <p className="text-sm text-gray-800">{detail.script_name}</p>
                </div>
              )}

              <div className="mb-4">
                <div className="mb-1.5 flex items-center justify-between gap-3">
                  <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                    Script
                  </p>
                  {detail.script && (
                    <button
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(detail.script).then(() => toast.success("Script copied"))}
                      className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                    >
                      <Copy className="h-3.5 w-3.5" />
                      Copy
                    </button>
                  )}
                </div>
                {detail.script ? (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-800 leading-relaxed whitespace-pre-wrap max-h-56 overflow-y-auto">
                    {detail.script}
                  </div>
                ) : (
                  <p className="text-sm italic text-gray-300">
                    No script provided
                  </p>
                )}
              </div>

              {/* ── 5. FORMAT & COMMENTS ────────────────────────────────────── */}
              {detail.format_required && (
                <div className="mb-4">
                  <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                    Format required
                  </p>
                  <p className="text-sm text-gray-700">
                    {detail.format_required}
                  </p>
                </div>
              )}

              {detail.comments && (
                <div className="mb-4">
                  <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                    Comments
                  </p>
                  <div className="border-l-4 border-purple-200 bg-purple-50/50 pl-3 py-2 pr-3 rounded-r-md text-sm text-gray-700 whitespace-pre-wrap">
                    {detail.comments}
                  </div>
                </div>
              )}

              <div className="mb-4">
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                  Hooks requested
                </p>
                <p className="text-sm text-gray-700">
                  {detail.hooks_count ?? 1}
                </p>
              </div>

              <Divider />

              {/* ── 6. REFERENCES ───────────────────────────────────────────── */}
              {(detail.reference_link ||
                detail.example_reference_link ||
                detail.bulk_entry_file_url) && (
                <div className="mb-4">
                  <SectionTitle>References</SectionTitle>
                  <div className="space-y-2">
                    {detail.reference_link && (
                      <div>
                        <p className="text-[11px] text-gray-400 mb-0.5">
                          Reference
                        </p>
                        <LinkList value={detail.reference_link} label="Link" />
                      </div>
                    )}
                    {detail.example_reference_link && (
                      <div>
                        <p className="text-[11px] text-gray-400 mb-0.5">
                          Example reference
                        </p>
                        <LinkList
                          value={detail.example_reference_link}
                          label="Link"
                        />
                      </div>
                    )}
                    {detail.bulk_entry_file_url && (
                      <div>
                        <p className="text-[11px] text-gray-400 mb-0.5">
                          Bulk file
                        </p>
                        <LinkList
                          value={detail.bulk_entry_file_url}
                          label="File"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              <Divider />

              {/* ── 7. META FOOTER ─ requested by / for / via / date ─────────── */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-gray-400 px-1">
                {detail.requested_by_name && (
                  <MetaTag label="By" value={detail.requested_by_name} />
                )}
                {(detail.requested_for_agency_name ||
                  detail.requested_for_agency_id) && (
                  <MetaTag
                    label="For"
                    value={
                      detail.requested_for_agency_name ||
                      `Agency #${detail.requested_for_agency_id}`
                    }
                  />
                )}
                {requestedVia && <MetaTag label="Via" value={requestedVia} />}
                {detail.created_on && (
                  <MetaTag
                    label="Created"
                    value={formatDisplayDateTime(detail.created_on)}
                  />
                )}
              </div>

              {/* ── 8. AD DETAILS ───────────────────────────────────────────── */}
              {detail.ad && (
                <>
                  <Divider />
                  <SectionTitle>Ad details</SectionTitle>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
                    <LabelValue
                      label="Adset name"
                      value={detail.ad.adset_name}
                    />
                    <LabelValue label="Category" value={detail.ad.category} />
                    <LabelValue label="Track" value={detail.ad.track} />
                    <LabelValue
                      label="Track #"
                      value={detail.ad.track_number}
                    />
                    {detail.ad.created_on && (
                      <LabelValue
                        label="Ad created"
                        value={formatDisplayDateTime(detail.ad.created_on)}
                      />
                    )}
                  </div>
                </>
              )}

              {(detail.module_type || detail.creative_format || detail.language) && (
                <>
                  <Divider />
                  <SectionTitle>Brief</SectionTitle>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
                    <LabelValue label="Language" value={detail.language} />
                    <LabelValue label="Module type" value={detail.module_type} />
                    <LabelValue label="Module" value={detail.module} />
                    <LabelValue label="Creative format" value={detail.creative_format} />
                    <LabelValue label="Visual format" value={detail.visual_format} />
                    <LabelValue label="Human / AI" value={detail.human_ai} />
                    <LabelValue label="Script type" value={detail.script_type} />
                    <LabelValue label="Script writer" value={detail.script_writer_name} />
                  </div>
                </>
              )}

              {/* ── 9. SPRINT PLAN ──────────────────────────────────────────── */}
              {detail.ad_sprint_plan && (
                <>
                  <Divider />
                  <SectionTitle>Sprint plan</SectionTitle>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
                    <LabelValue
                      label="File name"
                      value={detail.ad_sprint_plan.filename}
                    />
                    <LabelValue
                      label="Delivery date"
                      value={formatDisplayDate(
                        detail.ad_sprint_plan.delivery_date
                      )}
                    />
                    {detail.ad_sprint_plan.created_on && (
                      <LabelValue
                        label="Created on"
                        value={formatDisplayDateTime(
                          detail.ad_sprint_plan.created_on
                        )}
                      />
                    )}
                  </div>
                  {detail.ad_sprint_plan.asset_url && (
                    <div className="mt-3">
                      <p className="text-[11px] text-gray-400 mb-0.5">Asset</p>
                      <LinkList
                        value={detail.ad_sprint_plan.asset_url}
                        label="Open asset"
                      />
                    </div>
                  )}
                </>
              )}
            </CollapsibleSection>

            {/* QC last, and open by default for reviewers: it is the only
                part of this page anyone acts on. */}
            {playableDeliveries.length > 0 && (
              <CollapsibleSection
                id="sec-qc"
                title={canQc ? "Delivery QC" : "Delivered videos"}
                subtitle={`${playableDeliveries.length} video${playableDeliveries.length === 1 ? "" : "s"}`}
                open={!closedIds.has("sec-qc")}
                onToggle={() => toggleSection("sec-qc")}
              >
                {canQc ? (
                  /* One note box per video, stacked: watch it, write what is
                     wrong, save. Reviewers work down the list. */
                  <QcReview
                    requestId={requestId}
                    deliveries={playableDeliveries}
                    history={detail.qc_history ?? []}
                    busy={qcBusy}
                    onAction={handleQcAction}
                  />
                ) : (
                  /* No ad_qc.review: videos and the round history, read-only.
                     Two columns, since there are no note boxes to line up. */
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {playableDeliveries.map((d) => (
                        <DeliveredVideo key={d.id} delivery={d} />
                      ))}
                    </div>
                    <QcHistory history={detail.qc_history ?? []} />
                  </div>
                )}
              </CollapsibleSection>
            )}

            {moderatedDeliveries.length > 0 && (
              <CollapsibleSection
                id="sec-moderation"
                title="Moderation"
                subtitle={`${moderatedDeliveries.length} of ${detail.deliveries.length} hook${
                  detail.deliveries.length === 1 ? "" : "s"
                } moderated`}
                open={!closedIds.has("sec-moderation")}
                onToggle={() => toggleSection("sec-moderation")}
              >
                <ModerationRounds deliveries={moderatedDeliveries} />
              </CollapsibleSection>
            )}

            {launchedDeliveries.length > 0 && (
              <CollapsibleSection
                id="sec-launch"
                title="Launch"
                subtitle={`${launchedDeliveries.length} of ${detail.deliveries.length} hook${
                  detail.deliveries.length === 1 ? "" : "s"
                } launched`}
                open={!closedIds.has("sec-launch")}
                onToggle={() => toggleSection("sec-launch")}
              >
                <LaunchDetails deliveries={launchedDeliveries} />
              </CollapsibleSection>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdRequestDetailPage;
